import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, FileText, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../api';
import { MonthContext } from '../context/MonthContext';

const OneDrivePurchases = () => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [processedFiles, setProcessedFiles] = useState({});
  const [viewMode, setViewMode] = useState('pending');
  const { selectedMonth } = React.useContext(MonthContext);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const endpoint = viewMode === 'pending' ? '/onedrive/purchases' : '/onedrive/archived';
      const res = await api.get(endpoint);
      setFiles(res.data.files || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setProcessedFiles({});
    loadFiles();
  }, [viewMode]);

  const handleProcess = async (fileId) => {
    setProcessing(true);
    try {
      await api.post('/onedrive/process', { fileId, monthYear: selectedMonth });
      setProcessedFiles(prev => ({...prev, [fileId]: true}));
    } catch (err) {
      alert("Erreur lors du traitement du fichier OneDrive");
    } finally {
      setProcessing(false);
    }
  };

  const handleUnprocess = async (fileId) => {
    setProcessing(true);
    try {
      await api.post('/onedrive/unprocess', { fileId });
      setProcessedFiles(prev => ({...prev, [fileId]: false}));
    } catch (err) {
      alert("Erreur lors de l'annulation du traitement");
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessAll = async () => {
    setProcessing(true);
    try {
      for (const file of files) {
        if (!processedFiles[file.id]) {
            await api.post('/onedrive/process', { fileId: file.id, monthYear: selectedMonth });
            setProcessedFiles(prev => ({...prev, [file.id]: true}));
        }
      }
    } catch (err) {
      alert("Erreur lors du traitement en lot");
    } finally {
      setProcessing(false);
    }
  };

  const filteredFiles = React.useMemo(() => {
    if (viewMode === 'pending') return files;
    // En mode archives, on filtre si le fichier contient le mois sélectionné
    return files.filter(f => f.name.includes(selectedMonth));
  }, [files, viewMode, selectedMonth]);

  return (
    <div className="view-section">
      <div className="header" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 0.5rem 0' }}>Gestion (OneDrive)</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>Récupération des PDF déposés dans le dossier partagé.</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
             <button onClick={() => setViewMode('pending')} style={{ width: '140px', height: '40px', lineHeight: '38px', boxSizing: 'border-box', background: viewMode === 'pending' ? '#f59e0b' : 'var(--bg-secondary)', color: viewMode === 'pending' ? 'white' : 'var(--text-secondary)', border: '1px solid var(--glass-border)', borderRadius: '6px', fontSize: '1rem', fontWeight: viewMode === 'pending' ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0' }}>En attente</button>
             <button onClick={() => setViewMode('archived')} style={{ width: '140px', height: '40px', lineHeight: '38px', boxSizing: 'border-box', background: viewMode === 'archived' ? '#64748b' : 'var(--bg-secondary)', color: viewMode === 'archived' ? 'white' : 'var(--text-secondary)', border: '1px solid var(--glass-border)', borderRadius: '6px', fontSize: '1rem', fontWeight: viewMode === 'archived' ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0' }}>Factures fournisseurs</button>
             <button onClick={loadFiles} style={{ width: '140px', height: '40px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', background: 'var(--accent)', color: 'white', border: '1px solid var(--accent)', borderRadius: '6px', cursor: 'pointer', fontWeight: 500, fontSize: '1rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0', gap: '0.5rem' }}>
               <RefreshCw size={16} className={loading ? 'spinning' : ''} style={{ flexShrink: 0 }}/> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>Actualiser</span>
             </button>
          </div>
        </div>
      </div>

      <div className="glass-panel" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={20} color="#38bdf8" /> {viewMode === 'pending' ? 'Fichiers en attente' : `Fichiers archivés (${selectedMonth})`} ({filteredFiles.length})
          </h2>
          {filteredFiles.length > 0 && viewMode === 'pending' && (
            <button onClick={handleProcessAll} disabled={processing} className="btn" style={{ background: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {processing ? <RefreshCw size={16} className="spinning" /> : <Download size={16} />}
              Importer tous les achats
            </button>
          )}
        </div>

        {loading && filteredFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            <RefreshCw size={24} className="spinning" />
            <p>Recherche des fichiers...</p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
            <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p style={{ margin: 0, fontSize: '1.1rem' }}>{viewMode === 'pending' ? 'Le dossier "Gestion" est vide.' : `Aucune archive trouvée pour ${selectedMonth}.`}</p>
            <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Tous les achats ont été traités.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  {viewMode === 'pending' && <th style={{ width: '60px', textAlign: 'center' }}>Traité</th>}
                  <th>Nom du Fichier</th>
                  <th>Date d'ajout</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map(f => (
                  <tr key={f.id} style={{ opacity: processedFiles[f.id] ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                    {viewMode === 'pending' && (
                    <td style={{ textAlign: 'center' }}>
                      <input 
                        type="checkbox" 
                        checked={!!processedFiles[f.id]} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleProcess(f.id);
                          } else {
                            handleUnprocess(f.id);
                          }
                        }}
                        style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                        disabled={processing}
                      />
                    </td>
                    )}
                    <td style={{ fontWeight: 500, textDecoration: processedFiles[f.id] ? 'line-through' : 'none' }}>{f.name}</td>
                    <td>{new Date(f.createdDateTime).toLocaleString('fr-FR')}</td>
                    <td>
                      <button onClick={() => handleProcess(f.id)} disabled={processing || processedFiles[f.id]} className="btn" style={{ padding: '0.35rem 0.75rem', background: processedFiles[f.id] ? '#10b981' : (viewMode === 'archived' ? '#f59e0b' : '#3b82f6'), color: 'white', fontSize: '0.8rem', opacity: processedFiles[f.id] ? 0.8 : 1 }}>
                        {processedFiles[f.id] ? (viewMode === 'archived' ? 'Relancé' : 'Importé') : (viewMode === 'archived' ? 'Relancer' : 'Importer')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OneDrivePurchases;
